import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Copy } from "lucide-react";
import type { BlankKeyRow } from "../lib/codepush-import";

const COPIED_FEEDBACK_MS = 2000;

interface ImportedKeyListProps {
  appName: string;
  generated: BlankKeyRow[];
  found: BlankKeyRow[];
}

interface KeyGroupProps {
  sentence: string;
  rows: BlankKeyRow[];
  copiedName: string | null;
  copyLabel: (name: string) => string;
  onCopy: (name: string, storedKey: string) => void;
}

function namesOf(rows: BlankKeyRow[]): string {
  return rows.map((row) => row.name).join(", ");
}

function KeyGroup({ sentence, rows, copiedName, copyLabel, onCopy }: KeyGroupProps) {
  const shown = rows.flatMap(({ name, storedKey }) => (storedKey ? [{ name, storedKey }] : []));

  return (
    <>
      <p className="mt-3 flex items-start gap-2 text-xs text-subtle">
        <AlertTriangle size={14} className="mt-px shrink-0 text-warning" aria-hidden="true" />
        <span className="min-w-0 break-words">{sentence}</span>
      </p>
      {shown.length > 0 && (
        <ul className="mt-2 flex flex-col gap-2">
          {shown.map(({ name, storedKey }) => (
            <li key={name} className="flex items-center gap-2 rounded-lg bg-elevated p-2.5">
              <div className="min-w-0 flex-1">
                <span className="block break-words text-xs font-medium text-subtle">{name}</span>
                <code className="block break-all font-mono text-xs text-heading">{storedKey}</code>
              </div>
              <button
                type="button"
                onClick={() => onCopy(name, storedKey)}
                aria-label={copyLabel(name)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface hover:text-heading"
              >
                {copiedName === name ? (
                  <Check size={14} className="text-success" aria-hidden="true" />
                ) : (
                  <Copy size={14} aria-hidden="true" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function ImportedKeyList({ appName, generated, found }: ImportedKeyListProps) {
  const { t } = useTranslation();
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const [failedName, setFailedName] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyAttempt = useRef(0);

  useEffect(
    () => () => {
      copyAttempt.current += 1;
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const copyKey = async (name: string, storedKey: string) => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyAttempt.current += 1;
    const attempt = copyAttempt.current;
    setCopiedName(null);
    setFailedName(null);
    try {
      await navigator.clipboard.writeText(storedKey);
      if (attempt !== copyAttempt.current) return;
      setCopiedName(name);
      copyTimer.current = setTimeout(() => setCopiedName(null), COPIED_FEEDBACK_MS);
    } catch {
      if (attempt !== copyAttempt.current) return;
      setFailedName(name);
    }
  };

  const copyLabel = (name: string) => t("apps.import.copyKey", { name });
  const onCopy = (name: string, storedKey: string) => void copyKey(name, storedKey);
  const elsewhere = [...generated, ...found].filter((row) => !row.storedKey);

  return (
    <>
      {generated.length > 0 && (
        <KeyGroup
          sentence={t("apps.import.completedGeneratedKeys", {
            count: generated.length,
            names: namesOf(generated),
          })}
          rows={generated}
          copiedName={copiedName}
          copyLabel={copyLabel}
          onCopy={onCopy}
        />
      )}
      {found.length > 0 && (
        <KeyGroup
          sentence={t("apps.import.completedFoundKeys", {
            count: found.length,
            names: namesOf(found),
          })}
          rows={found}
          copiedName={copiedName}
          copyLabel={copyLabel}
          onCopy={onCopy}
        />
      )}
      <p role="status" className="sr-only">
        {copiedName ? t("apps.import.keyCopied", { name: copiedName }) : ""}
      </p>
      {failedName && (
        <p role="alert" className="mt-2 break-words text-xs text-danger">
          {t("apps.import.copyFailed", { name: failedName })}
        </p>
      )}
      {elsewhere.length > 0 && (
        <p className="mt-2 break-words text-xs text-subtle">
          {t("apps.import.completedKeysElsewhere", {
            count: elsewhere.length,
            names: namesOf(elsewhere),
            app: appName,
          })}
        </p>
      )}
    </>
  );
}
